"use client";

import { useCallback, useId, useRef, useState } from "react";

import type { CatalogMetaResponse } from "../types/catalog.type";
import { useDropdownDismiss } from "./useDropdownDismiss.hook";
import { useSortControl } from "./useSortControl.hook";

type Sorting = CatalogMetaResponse["header"]["sorting"];

export function useSortControlDesktop(sorting: Sorting) {
  const { currentSort, selectedLabel, applySort } = useSortControl(sorting);
  const [isBoxOpen, setIsBoxOpen] = useState(false);
  const [localSelection, setLocalSelection] = useState(currentSort);
  const boxRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const boxId = useId();

  const handleClose = useCallback(() => {
    setIsBoxOpen(false);
    triggerRef.current?.focus();
  }, []);

  const { maxHeight, placement } = useDropdownDismiss(
    isBoxOpen ? { boxRef, triggerRef, onClose: handleClose } : null,
  );

  const handleSelect = useCallback(
    (value: string) => {
      applySort(value);
      setIsBoxOpen(false);
      triggerRef.current?.focus();
    },
    [applySort],
  );

  const handleBoxToggle = useCallback(() => {
    setIsBoxOpen((prev) => {
      if (!prev) setLocalSelection(currentSort);
      return !prev;
    });
  }, [currentSort]);

  return {
    currentSort,
    localSelection,
    setLocalSelection,
    selectedLabel,
    isBoxOpen,
    boxRef,
    triggerRef,
    boxId,
    maxHeight,
    placement,
    handleSelect,
    handleBoxToggle,
  };
}
